import { useMemo } from 'react';
import { layoutDag } from '../../utils/dagLayout';
import { useHtmlDir } from '../organic/motion';
import HubNode from './HubNode';
import {
  ACTIVE_EDGES, ACTIVE_NODES, BANK_COLOR, BANK_ICON, BANK_STATES, BANK_UNITS, nodeCount, stateCount,
} from './stages';

/*
 * The hub's n8n canvas: two lanes of nodes with wires between them.
 *
 *   Lane 1, active isolates:  Collection ┐
 *                                        ├─> Lab & Growth ─> DNA & ID ─> Ready for bank ┐
 *                             Intake ────┘                                              │
 *   Lane 2, the bank:         Preservation & Bank ─┬─> one node per storage unit  <─────┘
 *
 * Each lane is placed by utils/dagLayout.js (the specimen graph's layout)
 * with its own node size; the wire from "ready for bank" down to the bank
 * is drawn by hand, since it joins two layouts. Nodes are HTML (they hold
 * buttons); the wires are one SVG behind them.
 *
 * Right to left, the whole canvas is mirrored -- each node's x, and the
 * wires through an SVG scale(-1, 1) -- so the flow runs in the reading
 * direction. A phone gets no canvas: index.css lets the nodes flow as a
 * column of cards with a short connector above each (.hub-node--wide).
 *
 * A `lab_scope` of 'none' (no lab visibility) shows only the triggers: the
 * lab and bank lanes would be nodes with nothing behind them.
 */

export const ACTIVE_NODE = { w: 196, h: 212 };
const ACTIVE_GAP = { x: 44, y: 16 };
const UNIT_NODE = { w: 196, h: 60 };
const UNIT_GAP = { x: 64, y: 10 };
const ROOT_H = 196;
const TITLE_H = 30;
const LANE_GAP = 44;
const PAD = 12;
// Room past the last column for the wire that leaves "ready for bank".
const EDGE_ROOM = 24;

/** A wire with rounded corners: right, down to `yMid`, back to x2, down, in. */
export function connectorPath(x1, y1, x2, y2, yMid, r = 12) {
  const out = x1 + EDGE_ROOM - 4;
  const into = x2 - EDGE_ROOM + 4;
  return [
    `M ${x1} ${y1}`,
    `H ${out - r}`, `Q ${out} ${y1} ${out} ${y1 + r}`,
    `V ${yMid - r}`, `Q ${out} ${yMid} ${out - r} ${yMid}`,
    `H ${into + r}`, `Q ${into} ${yMid} ${into} ${yMid + r}`,
    `V ${y2 - r}`, `Q ${into} ${y2} ${into + r} ${y2}`,
    `H ${x2}`,
  ].join(' ');
}

/** Where everything goes, in left-to-right coordinates. Pure. */
export function canvasLayout({ triggersOnly = false, showIntake = true } = {}) {
  const nodes = ACTIVE_NODES.filter((n) => (showIntake || n.key !== 'intake')
    && (!triggersOnly || n.key === 'collection' || n.key === 'intake'));
  const keys = new Set(nodes.map((n) => n.key));
  const active = layoutDag(
    nodes.map((n) => ({ id: n.key })),
    triggersOnly ? [] : ACTIVE_EDGES.filter((e) => keys.has(e.from) && keys.has(e.to)),
    { node: ACTIVE_NODE, gap: ACTIVE_GAP, pad: PAD },
  );
  // Two triggers alone sit side by side, not in one column.
  if (triggersOnly) {
    nodes.forEach((n, i) => { active.positions[n.key] = { x: PAD + i * (ACTIVE_NODE.w + ACTIVE_GAP.x), y: PAD }; });
    active.width = PAD * 2 + nodes.length * ACTIVE_NODE.w + (nodes.length - 1) * ACTIVE_GAP.x;
    active.height = PAD * 2 + ACTIVE_NODE.h;
  }
  const activeTop = 0;
  const activeHeight = TITLE_H + active.height;
  if (triggersOnly) {
    return { nodes, active, bank: null, bankDx: 0, width: active.width, height: activeHeight, activeTop, bankTop: null };
  }

  const bank = layoutDag(
    [{ id: 'bank' }, ...BANK_UNITS.map((u) => ({ id: `unit:${u}` }))],
    BANK_UNITS.map((u) => ({ id: `e-bank-${u}`, from: 'bank', to: `unit:${u}` })),
    { node: UNIT_NODE, gap: UNIT_GAP, pad: PAD },
  );
  // The root is drawn taller than a unit, about its layout centre, so its
  // wires still leave from the middle of its edge.
  // The bank lane sits EDGE_ROOM in, so the wire from lane 1 has room to
  // turn before it enters the root.
  const bankDx = EDGE_ROOM;
  const root = bank.positions.bank;
  const rootCentre = root.y + UNIT_NODE.h / 2;
  const rootBox = { x: root.x + bankDx, y: Math.max(PAD, rootCentre - ROOT_H / 2), w: ACTIVE_NODE.w, h: ROOT_H };
  const bankTop = activeHeight + LANE_GAP;
  const width = Math.max(active.width, bank.width + bankDx) + EDGE_ROOM;
  const height = bankTop + TITLE_H + Math.max(bank.height, rootBox.y + ROOT_H + PAD);

  // "Ready for bank" out-port, down between the lanes, into the root's in-port.
  const last = active.positions.to_bank;
  const connector = connectorPath(
    last.x + ACTIVE_NODE.w, activeTop + TITLE_H + last.y + ACTIVE_NODE.h / 2,
    rootBox.x, bankTop + TITLE_H + rootCentre,
    activeHeight + LANE_GAP / 2,
  );
  return { nodes, active, bank, bankDx, rootBox, width, height, activeTop, bankTop, connector };
}

export default function PipelineCanvas({
  hub, t, selected, triggersOnly = false, showIntake = true,
  onOpenNode, onState, isStatePressed = () => false,
  collection = {}, intake = {}, unsentCount = 0,
}) {
  const dir = useHtmlDir();
  const rtl = dir === 'rtl';
  const layout = useMemo(() => canvasLayout({ triggersOnly, showIntake }), [triggersOnly, showIntake]);
  const { width: W } = layout;
  // Mirrored for right-to-left: the same picture, flowing the other way.
  const x = (left, w) => (rtl ? W - left - w : left);

  // "My isolates" has no lab table for a count to filter: its counts are words.
  const stateItems = (node, key) => node.states.map((s) => {
    const filters = !triggersOnly && (node.filters ? node.filters.includes(s) : true);
    return {
      key: s,
      label: t(`states.${key}.${s}`),
      count: stateCount(hub, key, s),
      tip: filters ? t('tip.state') : undefined,
      pressed: filters && isStatePressed(key, s),
      onClick: filters ? () => onState(key, s) : undefined,
    };
  });

  const activeNode = (node) => {
    const pos = layout.active.positions[node.key];
    const style = { position: 'absolute', left: x(pos.x, ACTIVE_NODE.w), top: TITLE_H + pos.y,
      width: ACTIVE_NODE.w, minHeight: ACTIVE_NODE.h };
    const common = {
      id: node.key, icon: node.icon, color: node.color, style,
      title: t(`node.${node.key}.title`), subtitle: t(`node.${node.key}.sub`),
      count: nodeCount(hub, node), countLabel: t(`node.${node.key}.countLabel`),
      states: stateItems(node, node.key), wide: !['collection', 'intake'].includes(node.key),
      // Triggers start the flow; in "My isolates" nothing follows them.
      ports: { in: !['collection', 'intake'].includes(node.key), out: !triggersOnly },
    };
    if (node.key === 'collection') {
      return (
        <HubNode key={node.key} {...common} pressed={null} disabled={!collection.canOpen}
          tip={collection.canOpen ? t('tip.node.collection') : t('tip.node.collectionDisabled')}
          onOpen={collection.onOpen}
          note={unsentCount > 0 ? t('unsent', { count: unsentCount }) : null}>
          <button type="button" className="hub-mini" onClick={collection.onMap} title={t('tip.map')}
            data-testid="hub-collection-map">{t('actions.map')}</button>
          {collection.onGallery && (
            <button type="button" className="hub-mini" onClick={collection.onGallery} title={t('tip.gallery')}
              data-testid="hub-collection-gallery">{t('actions.gallery')}</button>
          )}
        </HubNode>
      );
    }
    if (node.key === 'intake') {
      return (
        <HubNode key={node.key} {...common} pressed={selected === 'intake'} tip={t('tip.node.intake')}
          onOpen={() => onOpenNode('intake')}>
          {intake.onNew && (
            <button type="button" className="hub-mini" onClick={intake.onNew} title={t('tip.newIntake')}
              data-testid="hub-intake-new">{t('actions.newIntake')}</button>
          )}
          {intake.onImport && (
            <button type="button" className="hub-mini" onClick={intake.onImport} title={t('tip.import')}
              data-testid="hub-intake-import">{t('actions.import')}</button>
          )}
        </HubNode>
      );
    }
    return (
      <HubNode key={node.key} {...common} pressed={selected === node.key} tip={t(`tip.node.${node.key}`)}
        onOpen={() => onOpenNode(node.key)} />
    );
  };

  const wires = [];
  if (!triggersOnly) {
    layout.active.paths.forEach((p) => wires.push({ id: p.id, d: p.d, dx: 0, dy: layout.activeTop + TITLE_H }));
    layout.bank.paths.forEach((p) => wires.push({ id: p.id, d: p.d, dx: layout.bankDx, dy: layout.bankTop + TITLE_H }));
  }

  return (
    <div className="hub-canvas-scroll">
      <div className={`hub-canvas${triggersOnly ? ' hub-canvas--triggers' : ''}`}
        style={{ width: layout.width, height: layout.height }} data-testid="hub-canvas" data-dir={dir}>
        {!triggersOnly && (
          <svg className="hub-wires" width={layout.width} height={layout.height} aria-hidden="true"
            focusable="false">
            <g transform={rtl ? `translate(${W} 0) scale(-1 1)` : undefined}>
              {wires.map((w) => (
                <path key={w.id} d={w.d} transform={`translate(${w.dx} ${w.dy})`} className="hub-wire" />
              ))}
              <path d={layout.connector} className="hub-wire hub-wire--drop" data-testid="hub-connector" />
            </g>
          </svg>
        )}

        <section className="hub-lane hub-lane--active" aria-labelledby="hub-lane-active"
          style={{ top: layout.activeTop, width: layout.width, height: TITLE_H + layout.active.height }}>
          <h3 id="hub-lane-active" className="hub-lane-title">
            {triggersOnly ? t('lane.mine') : t('lane.active')}
          </h3>
          {layout.nodes.map(activeNode)}
        </section>

        {!triggersOnly && (
          <section className="hub-lane hub-lane--bank" aria-labelledby="hub-lane-bank"
            style={{ top: layout.bankTop, width: layout.width, height: layout.height - layout.bankTop }}>
            <h3 id="hub-lane-bank" className="hub-lane-title">{t('lane.bank')}</h3>
            <HubNode id="bank" icon={BANK_ICON} color={BANK_COLOR} wide
              title={t('node.bank.title')} subtitle={t('node.bank.sub')}
              count={hub?.bank?.total ?? null} countLabel={t('node.bank.countLabel')}
              pressed={selected === 'bank'} tip={t('tip.node.bank')} onOpen={() => onOpenNode('bank')}
              states={BANK_STATES.map((s) => ({
                key: s, label: t(`states.bank.${s}`), count: stateCount(hub, 'bank', s), tip: t('tip.state'),
                pressed: isStatePressed('bank', s), onClick: () => onState('bank', s),
              }))}
              note={hub?.bank?.unattributed > 0 ? t('unattributed', { count: hub.bank.unattributed }) : null}
              style={{ position: 'absolute', left: x(layout.rootBox.x, layout.rootBox.w), top: TITLE_H + layout.rootBox.y,
                width: layout.rootBox.w, minHeight: layout.rootBox.h }} />
            {BANK_UNITS.map((u) => {
              const pos = layout.bank.positions[`unit:${u}`];
              const key = `unit:${u}`;
              return (
                <HubNode key={key} id={`unit-${u}`} icon={BANK_ICON} color={BANK_COLOR} compact ports={{ in: true, out: false }}
                  title={t(`specimens:storage.units.${u}`)} count={hub?.bank?.by_unit?.[u] ?? null}
                  countLabel={t('node.unit.countLabel')}
                  pressed={selected === key} tip={t('tip.node.unit')} onOpen={() => onOpenNode(key)}
                  style={{ position: 'absolute', left: x(pos.x + layout.bankDx, UNIT_NODE.w), top: TITLE_H + pos.y,
                    width: UNIT_NODE.w, minHeight: UNIT_NODE.h }} />
              );
            })}
          </section>
        )}
      </div>
    </div>
  );
}
